/**
 * Recording Session Store
 * Zustand store with persistence to track recording sessions
 * Enables recovery of interrupted recordings
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type RecordingSessionStatus = 'recording' | 'paused' | 'interrupted' | 'completed' | 'failed';

export interface RecordingSegment {
  id: string;
  segmentIndex: number;
  startTime: number; // timestamp
  endTime?: number;  // timestamp
  duration: number;  // ms
  chunkCount: number;
  mimeType: string;
}

export interface RecordingSession {
  sessionId: string;
  appointmentId?: string;
  encounterId?: string;
  patientId: string;
  providerId: string;
  patientName?: string; // For display in recovery UI
  startedAt: number; // timestamp
  lastActiveAt: number; // timestamp
  segments: RecordingSegment[];
  status: RecordingSessionStatus;
  totalDuration: number; // ms
  interruptionReason?: 'app_background' | 'phone_call' | 'network_error' | 'user_paused' | 'unknown';
  encryptionKeyId?: string; // Reference to encryption key for reconstruction
}

interface RecordingSessionState {
  // State
  activeSession: RecordingSession | null;
  interruptedSessions: RecordingSession[];

  // Actions - Session Management
  startSession: (sessionData: {
    sessionId: string;
    appointmentId?: string;
    encounterId?: string;
    patientId: string;
    providerId: string;
    patientName?: string;
    encryptionKeyId?: string;
    mimeType?: string;
  }) => void;

  pauseSession: (reason?: RecordingSession['interruptionReason']) => void;
  resumeSession: () => void;
  interruptSession: (reason: RecordingSession['interruptionReason']) => void;
  completeSession: () => void;
  failSession: (reason?: string) => void;
  clearActiveSession: () => void;

  // Actions - Segment Management
  addSegment: (segment: Omit<RecordingSegment, 'segmentIndex'>) => void;
  updateCurrentSegment: (updates: Partial<RecordingSegment>) => void;
  endCurrentSegment: () => void;

  // Actions - Duration Tracking
  updateDuration: (additionalMs: number) => void;
  setTotalDuration: (durationMs: number) => void;

  // Actions - Interrupted Sessions
  clearInterruptedSession: (sessionId: string) => void;
  clearAllInterruptedSessions: () => void;
  promoteInterruptedToActive: (sessionId: string) => RecordingSession | null;

  // Queries
  hasInterruptedSessions: () => boolean;
  getInterruptedSessionCount: () => number;
  getSessionById: (sessionId: string) => RecordingSession | null;
  getCurrentSegment: () => RecordingSegment | null;
}

export const useRecordingSessionStore = create<RecordingSessionState>()(
  persist(
    (set, get) => ({
      // Initial State
      activeSession: null,
      interruptedSessions: [],

      // Session Management
      startSession: (sessionData) => {
        const now = Date.now();
        const newSession: RecordingSession = {
          ...sessionData,
          startedAt: now,
          lastActiveAt: now,
          segments: [],
          status: 'recording',
          totalDuration: 0,
        };

        set({ activeSession: newSession });
      },

      pauseSession: (reason = 'user_paused') => {
        const { activeSession } = get();
        if (!activeSession || activeSession.status !== 'recording') {
          console.warn('[RecordingSession] Cannot pause - no active recording session');
          return;
        }

        set({
          activeSession: {
            ...activeSession,
            status: 'paused',
            lastActiveAt: Date.now(),
            interruptionReason: reason,
          }
        });
      },

      resumeSession: () => {
        const { activeSession } = get();
        if (!activeSession || activeSession.status !== 'paused') {
          console.warn('[RecordingSession] Cannot resume - no paused session');
          return;
        }

        set({
          activeSession: {
            ...activeSession,
            status: 'recording',
            lastActiveAt: Date.now(),
            interruptionReason: undefined,
          }
        });
      },

      interruptSession: (reason) => {
        const { activeSession, interruptedSessions } = get();
        if (!activeSession) {
          console.warn('[RecordingSession] Cannot interrupt - no active session');
          return;
        }

        console.log('[RecordingSession] Interrupting session:', activeSession.sessionId, 'reason:', reason);

        const interruptedSession: RecordingSession = {
          ...activeSession,
          status: 'interrupted',
          lastActiveAt: Date.now(),
          interruptionReason: reason,
        };

        set({
          activeSession: null,
          interruptedSessions: [...interruptedSessions, interruptedSession],
        });
      },

      completeSession: () => {
        const { activeSession } = get();
        if (!activeSession) {
          console.warn('[RecordingSession] Cannot complete - no active session');
          return;
        }

        console.log('[RecordingSession] Completing session:', activeSession.sessionId);

        set({
          activeSession: {
            ...activeSession,
            status: 'completed',
            lastActiveAt: Date.now(),
          }
        });
      },

      failSession: (reason) => {
        const { activeSession } = get();
        if (!activeSession) {
          console.warn('[RecordingSession] Cannot fail - no active session');
          return;
        }

        console.log('[RecordingSession] Session failed:', activeSession.sessionId, 'reason:', reason);

        set({
          activeSession: {
            ...activeSession,
            status: 'failed',
            lastActiveAt: Date.now(),
          }
        });
      },

      clearActiveSession: () => {
        set({ activeSession: null });
      },

      // Segment Management
      addSegment: (segmentData) => {
        const { activeSession } = get();
        if (!activeSession) {
          console.warn('[RecordingSession] Cannot add segment - no active session');
          return;
        }

        const segmentIndex = activeSession.segments.length;
        const newSegment: RecordingSegment = {
          ...segmentData,
          segmentIndex,
        };

        set({
          activeSession: {
            ...activeSession,
            segments: [...activeSession.segments, newSegment],
            lastActiveAt: Date.now(),
          }
        });
      },

      updateCurrentSegment: (updates) => {
        const { activeSession } = get();
        if (!activeSession || activeSession.segments.length === 0) {
          console.warn('[RecordingSession] Cannot update segment - no active segment');
          return;
        }

        const segments = [...activeSession.segments];
        const lastIndex = segments.length - 1;
        segments[lastIndex] = { ...segments[lastIndex], ...updates };

        set({
          activeSession: {
            ...activeSession,
            segments,
            lastActiveAt: Date.now(),
          }
        });
      },

      endCurrentSegment: () => {
        const { activeSession } = get();
        if (!activeSession || activeSession.segments.length === 0) {
          console.warn('[RecordingSession] Cannot end segment - no active segment');
          return;
        }

        const segments = [...activeSession.segments];
        const lastIndex = segments.length - 1;
        const currentSegment = segments[lastIndex];

        segments[lastIndex] = {
          ...currentSegment,
          endTime: Date.now(),
          duration: Date.now() - currentSegment.startTime,
        };

        set({
          activeSession: {
            ...activeSession,
            segments,
            lastActiveAt: Date.now(),
          }
        });
      },

      // Duration Tracking
      updateDuration: (additionalMs) => {
        const { activeSession } = get();
        if (!activeSession) return;

        set({
          activeSession: {
            ...activeSession,
            totalDuration: activeSession.totalDuration + additionalMs,
            lastActiveAt: Date.now(),
          }
        });
      },

      setTotalDuration: (durationMs) => {
        const { activeSession } = get();
        if (!activeSession) return;

        set({
          activeSession: {
            ...activeSession,
            totalDuration: durationMs,
            lastActiveAt: Date.now(),
          }
        });
      },

      // Interrupted Sessions
      clearInterruptedSession: (sessionId) => {
        const { interruptedSessions } = get();
        set({
          interruptedSessions: interruptedSessions.filter(s => s.sessionId !== sessionId),
        });
      },

      clearAllInterruptedSessions: () => {
        set({ interruptedSessions: [] });
      },

      promoteInterruptedToActive: (sessionId) => {
        const { interruptedSessions, activeSession } = get();

        if (activeSession) {
          console.warn('[RecordingSession] Cannot promote - already have active session');
          return null;
        }

        const session = interruptedSessions.find(s => s.sessionId === sessionId);
        if (!session) {
          console.warn('[RecordingSession] Session not found:', sessionId);
          return null;
        }

        console.log('[RecordingSession] Promoting interrupted session to active:', sessionId);

        const promotedSession: RecordingSession = {
          ...session,
          status: 'paused', // Set to paused so user can resume
          lastActiveAt: Date.now(),
        };

        set({
          activeSession: promotedSession,
          interruptedSessions: interruptedSessions.filter(s => s.sessionId !== sessionId),
        });

        return promotedSession;
      },

      // Queries
      hasInterruptedSessions: () => {
        return get().interruptedSessions.length > 0;
      },

      getInterruptedSessionCount: () => {
        return get().interruptedSessions.length;
      },

      getSessionById: (sessionId) => {
        const { activeSession, interruptedSessions } = get();

        if (activeSession?.sessionId === sessionId) {
          return activeSession;
        }

        return interruptedSessions.find(s => s.sessionId === sessionId) || null;
      },

      getCurrentSegment: () => {
        const { activeSession } = get();
        if (!activeSession || activeSession.segments.length === 0) {
          return null;
        }
        return activeSession.segments[activeSession.segments.length - 1];
      },
    }),
    {
      name: 'recording-session-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Persist both active and interrupted sessions
        // Active session that wasn't properly completed will be recovered as interrupted
        activeSession: state.activeSession,
        interruptedSessions: state.interruptedSessions,
      }),
      onRehydrate: () => {
        return (state, error) => {
          if (error) {
            console.error('[RecordingSession] Failed to rehydrate:', error);
            return;
          }

          if (state?.activeSession) {
            // If there's an active session from previous run, it means
            // the app was closed/crashed without proper shutdown
            // Move it to interrupted sessions for recovery
            const orphanedSession = state.activeSession;

            // Only move to interrupted if it was recording or paused (not completed/failed)
            if (orphanedSession.status === 'recording' || orphanedSession.status === 'paused') {
              console.log('[RecordingSession] Found orphaned session, moving to interrupted:', orphanedSession.sessionId);

              const interruptedSession: RecordingSession = {
                ...orphanedSession,
                status: 'interrupted',
                lastActiveAt: Date.now(),
                interruptionReason: orphanedSession.interruptionReason || 'unknown',
              };

              state.interruptedSessions = [...state.interruptedSessions, interruptedSession];
              state.activeSession = null;
            } else {
              state.activeSession = null;
            }
          }
        };
      },
    }
  )
);

// Utility hook for checking if recovery is needed
export const useHasInterruptedRecordings = () => {
  return useRecordingSessionStore(state => state.interruptedSessions.length > 0);
};

// Utility hook for getting interrupted session count
export const useInterruptedSessionCount = () => {
  return useRecordingSessionStore(state => state.interruptedSessions.length);
};
